import { CircularProgress, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useCallback, useEffect, useState } from "react";
import { useDataProvider, useInput } from "react-admin";
import { BusinessUnit } from "../../types";

interface Props {
  source: string;
  label?: string;
  disabled?: boolean;
}

const BusinessUnitInput: React.FC<Props> = (props) => {
  const {
    input: { value, onChange },
    meta: { touched, error },
  } = useInput(props);
  const dataProvider = useDataProvider();

  const [options, setOptions] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedValue, setSelectedValue] = useState<BusinessUnit | null>(null);

  const fetchUnits = useCallback(
    async (searchQuery: string, pageNum: number, append: boolean = false) => {
      setLoading(true);
      try {
        const { data, total } = await dataProvider.getList("business-units", {
          pagination: { page: pageNum, perPage: 25 },
          sort: { field: "name", order: "ASC" },
          filter: searchQuery ? { search: searchQuery } : {},
        });

        if (append) {
          setOptions((prev) => [...prev, ...data]);
        } else {
          setOptions(data);
        }
        setHasMore(pageNum * 25 < total);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [dataProvider],
  );

  // Initial load and search load
  useEffect(() => {
    fetchUnits(search, 1, false);
    setPage(1);
  }, [search, fetchUnits]);

  // Load more when page changes
  useEffect(() => {
    if (page > 1) {
      fetchUnits(search, page, true);
    }
  }, [page, search, fetchUnits]);

  // Fetch initial selected value if exists
  useEffect(() => {
    if (value && !selectedValue) {
      dataProvider
        .getOne("business-units", { id: value })
        .then(({ data }) => setSelectedValue(data))
        .catch(() => {});
    }
  }, [value, dataProvider, selectedValue]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 10
    ) {
      if (hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    }
  };

  return (
    <Autocomplete
      value={selectedValue}
      onChange={(_, newValue: BusinessUnit | null) => {
        setSelectedValue(newValue);
        onChange(newValue ? newValue.id : null);
      }}
      onInputChange={(_, newInputValue) => {
        setSearch(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.name || ""}
      getOptionSelected={(option, val) => option.id === val.id}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: "300px" },
      }}
      disabled={props.disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label || "Business Unit"}
          variant="filled"
          margin="normal"
          error={!!(touched && error)}
          helperText={touched && error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      fullWidth
    />
  );
};

export default BusinessUnitInput;
